import { useState, useMemo, useEffect } from 'react';
import CommonModal from '../../common/Modal';
import {
    useFirestoreNoCompagny,
    useFirestoreNoCompagnyGeneralOrderBy,
    useFirestoreOrderBy,
} from '../../../firebase/useFirestore';
import { client } from '../../../hooks/Client';
import {
    subscribeToTargetgroup,
    pairTargetgroupToExisting,
} from '../../synchronisations/synchronisationActions';
import linkedSourceIds from '../../../helpers/synchronisations/linkedSourceIds';

// Subscribe-side: a project picks targetgroups (Stakeholders) from a parent
// portfolio's catalog. Two flows per portfolio row:
//   1. "Doelgroep kopiëren"        → copy portfolio row into project + pair sync.
//   2. "Koppelen aan bestaande"    → pair the portfolio row to an EXISTING project
//                                    stakeholder. Only the sync doc is written;
//                                    the existing project stakeholder is untouched.
/**
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is open
 * @param {(open: boolean) => void} props.setModalOpen - Setter to close the modal
 * @param {string} props.preselectedPortfolioId - CompagnyID of the portfolio whose targetgroups are shown
 * @param {(added: Array<{id: string, docid: string}>) => void} [props.onSubscribed] - Called with the copied/paired local targetgroup after subscribe
 */
const SubscribePortfolioTargetgroupsModal = ({ modalOpen, setModalOpen, preselectedPortfolioId, onSubscribed }) => {
    const [selectedPortfolioId, setSelectedPortfolioId] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [pairingFor, setPairingFor] = useState(/** @type {object | null} */ (null));

    useEffect(() => {
        if (preselectedPortfolioId) setSelectedPortfolioId(preselectedPortfolioId);
    }, [preselectedPortfolioId]);

    const allPortfolioMeta = useFirestoreNoCompagny(
        'CompagnyMeta',
        'CompagnyID',
        selectedPortfolioId || '__none__'
    );
    const portfolioName = allPortfolioMeta?.[0]?.CommunityName || 'portfolio';

    const portfolioStakeholders = useFirestoreNoCompagnyGeneralOrderBy(
        'Stakeholders',
        'CompagnyID',
        selectedPortfolioId || '__none__',
        'Position',
        'asc'
    );
    const projectStakeholders = useFirestoreOrderBy('Stakeholders', 'Position', 'asc');
    const projectSyncs = useFirestoreNoCompagnyGeneralOrderBy(
        'Synchronisations',
        'Project',
        client,
        'Timestamp',
        'desc'
    );

    // Soft-deleted docs (Deleted: true, ADR-008) stay in Firestore but must
    // not be offered for linking.
    const portfolioTargetgroupsList = useMemo(
        () => (Array.isArray(portfolioStakeholders) ? portfolioStakeholders : []).filter((s) => !s?.Deleted),
        [portfolioStakeholders]
    );

    // A targetgroup this project already linked can't be copied or paired again
    // — that left a second link to the same portfolio item (#291). Locked until
    // the links are loaded.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const syncsLoaded = Array.isArray(projectSyncs) && Array.isArray(projectStakeholders);
    const linkedTargetgroupIds = useMemo(
        () => linkedSourceIds(projectSyncs, selectedPortfolioId, ['Targetgroup'], projectStakeholders),
        [projectSyncs, selectedPortfolioId, projectStakeholders]
    );

    const projectStakeholdersList = useMemo(
        () => (Array.isArray(projectStakeholders) ? projectStakeholders : []).filter((s) => !s?.Deleted),
        [projectStakeholders]
    );

    const closeModal = () => {
        setSelectedPortfolioId('');
        setBusy(false);
        setError('');
        setPairingFor(null);
        setModalOpen(false);
    };

    const handleSubscribe = async (targetgroup) => {
        if (busy) return;
        setBusy(true);
        setError('');

        let added;
        try {
            added = await subscribeToTargetgroup({
                portfolioTargetgroup: targetgroup,
                parentId: selectedPortfolioId,
                targetgroupsLength: (projectStakeholders || []).length,
                syncsLength: (projectSyncs || []).length,
            });
        } catch (err) {
            setError(err.message);
            setBusy(false);
            return;
        }

        if (typeof onSubscribed === 'function') onSubscribed([added]);
        closeModal();
    };

    const handlePairToExisting = async (projectTargetgroup) => {
        if (busy || !pairingFor) return;
        setBusy(true);
        setError('');

        let added;
        try {
            added = await pairTargetgroupToExisting({
                portfolioTargetgroup: pairingFor,
                projectTargetgroup,
                parentId: selectedPortfolioId,
                syncsLength: (projectSyncs || []).length,
            });
        } catch (err) {
            setError(err.message);
            setBusy(false);
            return;
        }

        if (typeof onSubscribed === 'function') onSubscribed([added]);
        closeModal();
    };

    const renderPickerView = () => (
        <div className="subscribe-modal__drilldown">
            <button
                type="button"
                className="subscribe-modal__back"
                onClick={() => setPairingFor(null)}
                disabled={busy}
            >
                ← Terug
            </button>
            <div className="subscribe-modal__drilldown-context">
                Koppelen aan: <b>{pairingFor.Organisation || '(zonder naam)'}</b>
            </div>
            {projectStakeholdersList.length === 0 ? (
                <p className="subscribe-modal__empty">Je hebt nog geen eigen doelgroepen.</p>
            ) : (
                <div className="subscribe-modal__list">
                    {projectStakeholdersList.map((stakeholder) => (
                        <div key={stakeholder.ID} className="subscribe-modal__item">
                            <div className="subscribe-modal__item-header">
                                <p className="subscribe-modal__item-title">
                                    {stakeholder.Organisation || '(zonder naam)'}
                                    {stakeholder.Categorie && (
                                        <span className="subscribe-modal__item-meta">
                                            {stakeholder.Categorie}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div className="subscribe-modal__item-actions">
                                <button
                                    className="subscribe-modal__btn subscribe-modal__btn--primary"
                                    disabled={busy}
                                    onClick={() => handlePairToExisting(stakeholder)}
                                >
                                    Selecteren
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    const renderPortfolioList = () => (
        portfolioTargetgroupsList.length === 0 ? (
            <p className="subscribe-modal__empty">Geen koppelbare doelgroepen gevonden in deze portfolio.</p>
        ) : (
            <div className="subscribe-modal__list">
                {portfolioTargetgroupsList.map((targetgroup) => {
                    const isLocked = !syncsLoaded || linkedTargetgroupIds.has(targetgroup.ID);
                    return (
                    <div key={targetgroup.ID} className="subscribe-modal__item">
                        <div className="subscribe-modal__item-header">
                            <p className="subscribe-modal__item-title">
                                {targetgroup.Organisation || '(zonder naam)'}
                                {targetgroup.Categorie && (
                                    <span className="subscribe-modal__item-meta">
                                        {targetgroup.Categorie}
                                    </span>
                                )}
                                {linkedTargetgroupIds.has(targetgroup.ID) && (
                                    <span className="subscribe-modal__linked-badge">Al gekoppeld</span>
                                )}
                            </p>
                        </div>
                        <div className="subscribe-modal__item-actions">
                            <button
                                className="subscribe-modal__btn subscribe-modal__btn--primary"
                                disabled={busy || isLocked}
                                onClick={() => handleSubscribe(targetgroup)}
                            >
                                Doelgroep kopiëren
                            </button>
                            <button
                                className="subscribe-modal__btn"
                                disabled={busy || isLocked}
                                onClick={() => setPairingFor(targetgroup)}
                            >
                                Koppelen aan bestaande
                            </button>
                        </div>
                    </div>
                    );
                })}
            </div>
        )
    );

    return (
        <CommonModal
            modalOpen={modalOpen}
            setModalOpen={(value) => { if (!value) closeModal(); else setModalOpen(true); }}
            pageHeader={selectedPortfolioId ? `Doelgroepen uit ${portfolioName} koppelen` : 'Doelgroepen uit portfolio koppelen'}
            hideConfirm={true}
            content={
                <div className="subscribe-modal">
                    {error && <p className="subscribe-modal__error">{error}</p>}
                    {!selectedPortfolioId ? (
                        <p className="subscribe-modal__empty">Geen portfolio geselecteerd.</p>
                    ) : pairingFor ? (
                        renderPickerView()
                    ) : (
                        renderPortfolioList()
                    )}
                </div>
            }
        />
    );
};

export default SubscribePortfolioTargetgroupsModal;
